'use client';

import { Honeybadger } from '@honeybadger-io/react';
import { formatUrl } from '@mnd-frontend/ui/public/utils/formatters/url';
import { mapLangToLocale } from '@mnd-frontend/ui/public/utils/maps/locale';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import {
  countryKeys,
  countryTranslationKeys,
  prioritizedCountryKeys,
} from '@mnd-frontend/ui/shared/lists/countries';
import isEmail from 'nice-is-email';
import { type FormEvent, useCallback, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { PASSWORD_MIN_LENGTH } from '../../constants';
import { isBlockedEmailDomain } from '../../lib/emailDomainBlockList';
import { ReCaptchaProvider, useReCaptcha } from '../../lib/next-recaptcha-v3';
import { selectContentClickHandler, track } from '../../lib/tracker';
import { routes } from '../../routes';
import { getCookieByName } from '../../utils/cookies';
import logger from '../../utils/logger';
import Button from '../Button';
import Checkbox from '../Checkbox';
import FormTitleBanner from '../FormTitleBanner';
import { ClientOnlyInput } from '../Input';
import Select from '../Select';

const Form = styled.form<{ $hasTitle: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-small);
  background: var(--color-white);
  padding: var(--card-padding) var(--card-padding-small);

  ${({ $hasTitle }) =>
    $hasTitle &&
    css`
      position: relative;
    `}

  @media (min-width: 768px) {
    padding: var(--card-padding);
  }
  a {
    color: var(--link-color);
  }
`;

const FormButton = styled(Button)`
  width: 100%;
  align-self: flex-start;
  font-size: var(--font-size-large);
  margin-top: var(--spacing-medium);
  padding: 1.5rem 1.25rem;

  @media (min-width: 960px) {
    width: auto;
  }
`;

const FormPolicyText = styled.span`
  font-size: 0.75rem;
  color: var(--color-gray);
`;

const InlineLink = styled.a`
  color: var(--link-color);
  text-decoration: underline;
`;

type FormMetaData = {
  title: string | null;
  description: string | null;
  submitButton: string | null;
  acceptTerms: string | null;
  terms: string | null;
  newsletterOptIn: string | null;
  bgColor: string | null;
};

type FormFields = {
  name: string;
  email: string;
  password: string;
  phone: string;
  company_name: string;
  site_code: string;
  newsletter_opt_in: boolean;
  accept_terms: boolean;
};

const initialEmptyForm: FormFields = {
  name: '',
  email: '',
  password: '',
  phone: '',
  company_name: '',
  site_code: '',
  newsletter_opt_in: false,
  accept_terms: false,
};

const nameSanitationPattern = /[^0-9!#%&:;<=>@_~^$*+?()[\]{}\\|/]/g;

const sanitizeByPattern = (value: string, pattern: RegExp) => {
  return value.match(pattern)?.join('') || '';
};

const TermsText = ({
  formData,
  lang,
}: {
  formData: FormMetaData | null | undefined;
  lang: string;
}) => {
  if (formData && formData.terms) {
    return (
      <span>
        <a
          href={formatUrl(routes.termsConditions(lang))}
          onClick={selectContentClickHandler('terms', 'terms')}
          dangerouslySetInnerHTML={{ __html: formData.acceptTerms || '' }}
        />
      </span>
    );
  } else {
    return (
      <span>
        <Trans
          i18nKey="form.termsAndConditions"
          components={{
            a: (
              <InlineLink
                href={routes.termsConditions(lang)}
                onClick={selectContentClickHandler('terms_and_conditions', 'terms')}
                target="_blank"
                rel="noopener noreferrer"
              />
            ),
          }}
        />
      </span>
    );
  }
};

const SignupForm = ({ formData }: { formData?: FormMetaData | null }) => {
  const { executeRecaptcha } = useReCaptcha();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { showFailureToast } = useToasts();
  const [loading, setLoading] = useState(false);
  const formTitle = formData?.title;
  const formId = 'mnd-signup-form-base';
  const [formErrors, setFormErrors] = useState<{ [key: string]: string[] }>({});
  const [validFields, setValidFields] = useState<{ [key: string]: boolean }>({});
  const [fields, setFields] = useState<FormFields>(initialEmptyForm);
  const formRef = useRef<HTMLFormElement>(null);

  const handleRecpatchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      logger.error('ReCAPTCHA not available');
      return;
    }
    const token = await executeRecaptcha('TrialSignup');
    return token;
  }, [executeRecaptcha]);

  const submitButtonText = formData?.submitButton ? formData.submitButton : t('form.formCta');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    const { email, name, company_name, phone, site_code, ...rest } = fields;

    const hubspotCookie = getCookieByName('hubspotutk');
    const token = await handleRecpatchaVerify();

    const trackError = (error_code: string, error_message: string, form_field: string) => {
      track({
        event: 'error_form_submit',
        traits: {
          event_info: {
            form_id: formId,
            error_code,
            error_message,
            form_field,
            form_name: formTitle ?? '',
            navigation_selection: submitButtonText,
            user_interaction: 'cta_click',
          },
        },
      });
    };

    if (!token) {
      showFailureToast('Recaptcha validation failed');
      setLoading(false);
      trackError('captcha', 'Failed to verify recaptcha token', 'captcha');
      return;
    }

    try {
      const verifyRecaptchaResponse = await fetch('/api/verify-captcha', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });
      if (!verifyRecaptchaResponse.ok) {
        showFailureToast('Failed to verify recaptcha token');
        setLoading(false);
        trackError('captcha', 'Failed to verify recaptcha token', 'captcha');
        return;
      }
    } catch (_e) {
      showFailureToast('Failed to verify recaptcha token');
      setLoading(false);
      trackError('captcha', 'Failed to verify recaptcha token', 'captcha');
      return;
    }

    const normalizedData = {
      site_code: site_code.toLowerCase(),
      locale: mapLangToLocale(lang),
      hubspot_session: hubspotCookie,
      email: email.trim(),
      name: name.trim(),
      company_name: company_name.trim(),
      phone: phone.trim(),
      ...rest,
    };

    try {
      const resp = await fetch('/api/v1/marketing/trial_signups', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(normalizedData),
      });
      if (resp.ok) {
        try {
          const mktParam = new URLSearchParams(window.location.search).get('mkt');

          track({
            event: 'Trial Signup - Form Submitted',
            traits: {
              market: mktParam ?? '',
            },
          });
          track({
            event: 'lead_generation',
            traits: {
              event_info: {
                form_id: formId,
                lead_source: '',
                navigation_selection: submitButtonText,
                form_name: formTitle || formId,
                user_interaction: 'lead_generation',
              },
            },
          });
        } catch (e) {
          console.error(e);
        }
        const respData: { redirect_url: string } = await resp.json();
        window.location.href = respData.redirect_url;
      } else {
        const respData: {
          error_messages: { [key: string]: string[] };
          error_types: { [key: string]: string[] };
        } = await resp.json();

        if (Object.keys(respData.error_messages).length) {
          const blockedEmail = respData.error_types['email'].indexOf('blocked');
          if (blockedEmail > -1) {
            respData.error_messages['email'][blockedEmail] = t('form.validation.email.blocked', {
              domain: email.split('@')[1],
            });
          }

          setFormErrors(respData.error_messages);
          Object.entries(respData.error_messages).forEach(([key, messages]) => {
            setValidField(key, false);
            messages.forEach(msg => {
              trackError(msg, msg, key);
            });
          });
        } else {
          trackError('unknown', 'Something went wrong.', 'unknown');
        }
      }
    } catch (e) {
      showFailureToast('Something went wrong.');
      trackError('unknown', 'Something went wrong.', 'unknown');
      Honeybadger.notify(e as Error, {
        message: 'Trial signup failed',
      });
    }
    setLoading(false);
  };

  const countryOptions = useMemo(() => {
    return [
      ...(prioritizedCountryKeys
        .map(key => {
          const translatedValue = t(countryTranslationKeys[key]);
          if (!translatedValue.startsWith('locale')) {
            return [key, translatedValue];
          }
          return null;
        })
        .filter(entry => entry) as [string, string][]),
      ['---', '---'],
      ...(countryKeys
        .map(key => {
          const translatedValue = t(countryTranslationKeys[key]);
          if (!translatedValue.startsWith('locale')) {
            return [key, translatedValue];
          }
          return null;
        })
        .filter(entry => entry) as [string, string][]),
    ];
  }, [t]);

  const setFormError = (name: string, error?: string) => {
    setFormErrors(prev => {
      const next = { ...prev };
      if (error) {
        next[name] = [error];
      } else {
        delete next[name];
      }
      return next;
    });
  };

  const setValidField = (name: string, valid: boolean) => {
    setValidFields(prev => {
      const next = { ...prev };
      if (valid) {
        next[name] = valid;
      } else {
        delete next[name];
      }
      return next;
    });
  };

  const validatePassword = (password: string) => {
    const hasLowerCase = password !== password.toUpperCase();
    const hasUpperCase = password !== password.toLowerCase();
    const hasNumber = /\d/.test(password);

    if (
      password.length < PASSWORD_MIN_LENGTH ||
      password.length === 0 ||
      !hasLowerCase ||
      !hasUpperCase ||
      !hasNumber
    ) {
      setFormError('password', t('form.validation.password.format'));
      setValidField('password', false);
      return false;
    } else {
      setFormError('password');
      setValidField('password', true);
      return true;
    }
  };

  const validateEmail = (email: string) => {
    if (!isEmail(email)) {
      setFormError('email', t('form.validation.email.format'));
      setValidField('email', false);
      return false;
    } else if (isBlockedEmailDomain(email)) {
      setFormError('email', t('form.validation.email.blocked', { domain: email.split('@')[1] }));
      setValidField('email', false);
      return false;
    } else {
      setFormError('email');
      setValidField('email', true);
      return true;
    }
  };

  const validatePresence = (
    name: string,
    value: string,
    translationKey = 'form.validation.required',
  ) => {
    if (!value || value.trim().length < 1) {
      setFormError(name, t(translationKey));
      setValidField(name, false);
      return false;
    } else {
      setFormError(name);
      setValidField(name, true);
      return true;
    }
  };

  const validateForm = () => {
    const isValidName = validatePresence('name', fields.name);
    const isValidEmail = validateEmail(fields.email);
    const isValidPassword = validatePassword(fields.password);
    const isValidCompanyName = validatePresence('company_name', fields.company_name);
    const isValidPhone = validatePresence('phone', fields.phone);
    const isValidSiteCode = validatePresence(
      'site_code',
      fields.site_code,
      'form.validation.siteCode.required',
    );

    return (
      isValidName &&
      isValidEmail &&
      isValidPassword &&
      isValidCompanyName &&
      isValidPhone &&
      isValidSiteCode
    );
  };

  // This is to allow the HTML5 validation to be triggered
  // alongside the custom validation
  const preValidateForm = () => {
    const _formData = new FormData(formRef.current!);
    validateForm();
    formRef.current?.requestSubmit();
  };

  const handleFieldChange = (field: string, value: string | boolean) => {
    setFields(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Form ref={formRef} onSubmit={handleSubmit} $hasTitle={!!formTitle} id={formId}>
      {formTitle && <FormTitleBanner title={formTitle} background={formData.bgColor} />}
      <ClientOnlyInput
        name="name"
        type="text"
        label={t('form.name')}
        errors={formErrors?.name}
        value={fields.name}
        onChange={e => {
          handleFieldChange(
            'name',
            sanitizeByPattern(e.currentTarget.value, nameSanitationPattern),
          );
          setFormError('name');
        }}
        onBlur={e => validatePresence('name', e.target.value)}
        valid={validFields?.name}
        maxLength={255}
      />
      <ClientOnlyInput
        name="email"
        type="email"
        label={t('form.email')}
        errors={formErrors?.email}
        value={fields.email}
        onChange={e => {
          handleFieldChange('email', e.currentTarget.value);
          setFormError('email');
        }}
        onBlur={e => validateEmail(e.target.value)}
        valid={validFields?.email}
        maxLength={255}
      />
      <ClientOnlyInput
        name="password"
        type="password"
        label={t('form.password')}
        value={fields.password}
        onChange={e => {
          handleFieldChange('password', e.currentTarget.value);
          setFormError('password');
        }}
        onBlur={e => validatePassword(e.target.value)}
        errors={formErrors?.password}
        valid={validFields?.password}
        maxLength={255}
      />
      <ClientOnlyInput
        name="phone"
        type="tel"
        label={t('form.phone')}
        errors={formErrors?.phone}
        value={fields.phone}
        onChange={e => {
          handleFieldChange('phone', sanitizeByPattern(e.currentTarget.value, /[0-9\- ()[\]]/g));
          setFormError('phone');
        }}
        onBlur={e => validatePresence('phone', e.target.value)}
        valid={validFields?.phone}
        maxLength={255}
      />
      <ClientOnlyInput
        name="company_name"
        type="text"
        label={t('form.companyName')}
        errors={formErrors?.company_name}
        value={fields.company_name}
        onChange={e => {
          handleFieldChange('company_name', e.currentTarget.value);
          setFormError('company_name');
        }}
        onBlur={e => validatePresence('company_name', e.target.value)}
        valid={validFields?.company_name}
        maxLength={255}
      />
      <Select
        name="site_code"
        label={t('form.chooseCountry')}
        options={countryOptions}
        errors={formErrors?.site_code}
        value={fields.site_code}
        onChange={e => {
          handleFieldChange('site_code', e.currentTarget.value);
        }}
        onBlur={e =>
          validatePresence('site_code', e.target.value, 'form.validation.siteCode.required')
        }
        valid={validFields?.site_code}
      />
      <Checkbox
        name="newsletter_opt_in"
        label={formData?.newsletterOptIn ? formData.newsletterOptIn : t('form.marketingConsent')}
        checked={fields.newsletter_opt_in}
        onChange={e => handleFieldChange('newsletter_opt_in', e.currentTarget.checked)}
      />
      <Checkbox
        name="accept_terms"
        label={<TermsText formData={formData} lang={lang} />}
        required
        checked={fields.accept_terms}
        onChange={e => handleFieldChange('accept_terms', e.currentTarget.checked)}
      />
      <FormButton onClick={preValidateForm} variant="primary" type="submit" disabled={loading}>
        {submitButtonText}
      </FormButton>
      <FormPolicyText>
        <Trans
          i18nKey="form.personalDataUsage"
          components={{
            link1: (
              <InlineLink
                href={routes.privacyPolicy(lang)}
                onClick={selectContentClickHandler('privacy_policy', 'terms')}
                target="_blank"
                rel="noopener noreferrer"
              />
            ),
            link2: (
              <InlineLink
                href={routes.termsUse(lang)}
                onClick={selectContentClickHandler('terms_of_use', 'terms')}
                target="_blank"
                rel="noopener noreferrer"
              />
            ),
          }}
        />
      </FormPolicyText>
    </Form>
  );
};

export const SignupFormWrapper = ({ formData }: { formData?: FormMetaData | null }) => {
  return (
    <ReCaptchaProvider
      reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''}
      async={false}
      defer={false}
      className="optanon-category-C0001"
    >
      <SignupForm formData={formData} />
    </ReCaptchaProvider>
  );
};
